import { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import Checkout from "./Checkout";

const CheckoutWrapper = ({ stripePromise }) => {
  const { planId, clientSecret: routeSecret } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Applying a promotion code rebuilds the subscription server-side, so the
  // Payment Element has to be re-mounted against the new invoice's secret.
  // The route param stays the source of truth (a refresh must not resurrect a
  // cancelled subscription), and the applied code rides along in router state.
  const [promo, setPromo] = useState(location.state?.promo ?? null);

  const PLAN_CODES = useMemo(
    () => ({
      aJ3d8Lk29vX4: "Pro",        // Monthly
      N9sJ0qWz7eB1: "Pro_Annual", // Annual
    }),
    []
  );

  const planKey = PLAN_CODES[planId] || "Pro"; // fallback

  const options = useMemo(
    () => ({
      clientSecret: routeSecret,
      appearance: { theme: "stripe" },
    }),
    [routeSecret]
  );

  const handlePromoApplied = (payload) => {
    // A 100%-off code settles the $0 invoice outright — there is no card step.
    if (payload.paid) {
      navigate("/payment-successful", { state: { fromCheckout: true } });
      return;
    }

    const next = payload.promoCode
      ? { promoCode: payload.promoCode, amounts: payload.amounts }
      : null;

    setPromo(next);
    navigate(`/checkout/${planId}/${payload.clientSecret}`, {
      replace: true,
      state: { promo: next },
    });
  };

  return (
    // Stripe does not allow swapping clientSecret on a live Elements instance,
    // so the key forces a fresh one whenever the secret changes.
    <Elements key={routeSecret} stripe={stripePromise} options={options}>
      <Checkout
        planKey={planKey}
        clientSecret={routeSecret}
        promo={promo}
        onPromoApplied={handlePromoApplied}
      />
    </Elements>
  );
};

export default CheckoutWrapper;
